/**
 * WebSocket service for real-time feedback updates
 */

export interface WebSocketMessage {
  type: 'feedback_update' | 'system' | 'connection' | 'ping' | 'pong' | 'error';
  event?: string;
  data?: any;
  message?: string;
  timestamp: string;
}

export interface FeedbackUpdateMessage extends WebSocketMessage {
  type: 'feedback_update';
  event: 'new_feedback' | 'status_change' | 'new_comment';
  data: any;
}

export type WebSocketEventHandler = (message: WebSocketMessage) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private eventHandlers: Set<WebSocketEventHandler> = new Set();
  private connectionState: 'disconnected' | 'connecting' | 'connected' | 'error' = 'disconnected';
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
    // Convert HTTP to WebSocket protocol
    this.baseUrl = this.baseUrl.replace('http://', 'ws://').replace('https://', 'wss://');
  }

  /**
   * Connect to WebSocket server
   */
  async connect(token?: string): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    this.isConnecting = true;
    this.connectionState = 'connecting';
    this.notifyHandlers({
      type: 'connection',
      event: 'connecting',
      message: 'Connecting to WebSocket server...',
      timestamp: new Date().toISOString()
    });

    try {
      const wsUrl = `${this.baseUrl}/api/v1/websocket/ws${token ? `?token=${token}` : ''}`;
      console.log('Connecting to WebSocket:', wsUrl);
      
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.isConnecting = false;
        this.connectionState = 'connected';
        this.reconnectAttempts = 0;
        this.notifyHandlers({
          type: 'connection',
          event: 'connected',
          message: 'Connected to WebSocket server',
          timestamp: new Date().toISOString()
        });
      };

      this.ws.onmessage = (event) => {
        try {
          // Try to fix common Python->JSON issues before parsing
          let cleanData = event.data;
          
          // Fix Python None -> null
          cleanData = cleanData.replace(/:\s*None([,}])/g, ': null$1');
          // Fix Python True/False -> true/false  
          cleanData = cleanData.replace(/:\s*True([,}])/g, ': true$1');
          cleanData = cleanData.replace(/:\s*False([,}])/g, ': false$1');
          
          const message: WebSocketMessage = JSON.parse(cleanData);
          console.log('WebSocket message received:', message);
          this.notifyHandlers(message);
          
          // Handle ping messages
          if (message.type === 'ping') {
            this.sendPong();
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
          console.error('Raw message data:', event.data);
          
          // Send a more graceful error message to handlers
          this.notifyHandlers({
            type: 'error',
            event: 'parse_error',
            message: 'Failed to parse WebSocket message',
            timestamp: new Date().toISOString()
          });
        }
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        this.isConnecting = false;
        this.connectionState = 'disconnected';
        this.ws = null;
        
        this.notifyHandlers({
          type: 'connection',
          event: 'disconnected',
          message: `Disconnected from WebSocket server (${event.code})`,
          timestamp: new Date().toISOString()
        });

        // Attempt to reconnect
        this.attemptReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.isConnecting = false;
        this.connectionState = 'error';
        
        this.notifyHandlers({
          type: 'error',
          message: 'WebSocket connection error',
          timestamp: new Date().toISOString()
        });
      };

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.isConnecting = false;
      this.connectionState = 'error';
      
      this.notifyHandlers({
        type: 'error',
        message: 'Failed to create WebSocket connection',
        timestamp: new Date().toISOString()
      });
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connectionState = 'disconnected';
    this.reconnectAttempts = 0;
  }

  /**
   * Send a message to the WebSocket server
   */
  send(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, cannot send message:', message);
    }
  }

  /**
   * Send ping to server
   */
  sendPing(): void {
    this.send({
      type: 'ping',
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Send pong response to server
   */
  sendPong(): void {
    this.send({
      type: 'pong',
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Subscribe to feedback events
   */
  subscribeToFeedback(): void {
    this.send({
      type: 'subscribe',
      subscription: 'feedback_updates',
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Add event handler
   */
  addEventHandler(handler: WebSocketEventHandler): void {
    this.eventHandlers.add(handler);
  }

  /**
   * Remove event handler
   */
  removeEventHandler(handler: WebSocketEventHandler): void {
    this.eventHandlers.delete(handler);
  }

  /**
   * Get current connection state
   */
  getConnectionState(): 'disconnected' | 'connecting' | 'connected' | 'error' {
    return this.connectionState;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Notify all event handlers
   */
  private notifyHandlers(message: WebSocketMessage): void {
    this.eventHandlers.forEach(handler => {
      try {
        handler(message);
      } catch (error) {
        console.error('Error in WebSocket event handler:', error);
      }
    });
  }

  /**
   * Attempt to reconnect with exponential backoff
   */
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    setTimeout(() => {
      this.connect();
    }, delay);
  }
}

// Create singleton instance
export const websocketService = new WebSocketService();
export default websocketService;